/**
 * Phase 2L-A16 — Lab Tech Dashboard.
 *
 * Task-focused workspace for lab technicians (+ admins). Pulls from
 * `/api/lab/dashboard/analytics`. By design this page never shows
 * revenue, payment, SEO, or admin maintenance data.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical, Inbox, ClipboardEdit, AlertOctagon, Eye, FileCheck2,
  Bug, RotateCcw, TimerReset, Activity, ArrowRight,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import HomeHealthSummary from "@/components/HomeHealthSummary";
import api from "@/lib/api";
import { formatDateAU, formatDateTimeAU } from "@/lib/formatDateAU";
import {
  DashboardKpi, DashboardSection, EmptyState, LoadingSkeleton,
  FunnelBar, ActivityFeed,
} from "@/components/dashboard/Primitives";

const RANGES = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

export default function LabDashboard() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Aware Lab Dashboard";
    let alive = true;
    setLoading(true);
    setError(null);
    api.get(`/lab/dashboard/analytics?range=${range}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load dashboard"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);

  const k = data?.kpis;
  const q = data?.queues;

  return (
    <PortalShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="mb-7 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Lab Tech</div>
            <h1 data-testid="lab-dashboard-heading" className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2">Aware Lab Dashboard</h1>
            <p className="text-sm text-slate-500 mt-2">What needs doing next. Click a tile or row to jump straight into the queue.</p>
          </div>
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="lab-dashboard-range-selector">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`lab-dashboard-range-${r.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${range === r.key ? "bg-cyan-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
              >{r.label}</button>
            ))}
          </div>
        </div>

        {/* Quick actions rail */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6" data-testid="lab-quick-actions">
          <QuickAction icon={Inbox} label="Receive samples" to="/admin/lab/bulk" testId="qa-receive" />
          <QuickAction icon={FlaskConical} label="Lab queue" to="/admin/lab" testId="qa-queue" />
          <QuickAction icon={ClipboardEdit} label="Results entry" to="/admin/lab?status=received" testId="qa-results" />
          <QuickAction icon={Eye} label="Awaiting review" to="/admin/lab?status=awaiting_review" testId="qa-review" />
          <QuickAction icon={FileCheck2} label="Published reports" to="/admin/lab?status=published" testId="qa-published" />
        </div>

        {/* Phase 2L-A40 — Today's Attention summary. Lab tech sees only
            the three lab-focused counters (samples awaiting receipt,
            samples in queue, reports awaiting review). */}
        <div className="mb-6">
          <HomeHealthSummary />
        </div>

        {loading ? <LoadingSkeleton /> : error ? (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-700">{error}</div>
        ) : !data ? <EmptyState /> : (
          <>
            {/* KPI strip — 10 tiles */}
            <div data-testid="lab-dashboard-kpis" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              <DashboardKpi label="Pending drop-off" value={k.samples_pending_dropoff} icon={FlaskConical} testId="kpi-lab-pending" to="/admin/lab?status=pending_sample" />
              <DashboardKpi label="Received today" value={k.samples_received_today} icon={Inbox} testId="kpi-lab-received" />
              <DashboardKpi label="Awaiting results entry" value={k.tests_awaiting_result_entry} icon={ClipboardEdit} tone={k.tests_awaiting_result_entry > 0 ? "warn" : "default"} testId="kpi-lab-awaiting-entry" to="/admin/lab?status=received" />
              <DashboardKpi label="Missing required fields" value={k.tests_missing_required_fields} icon={AlertOctagon} tone={k.tests_missing_required_fields > 0 ? "warn" : "default"} testId="kpi-lab-missing-required" />
              <DashboardKpi label="Awaiting review" value={k.tests_awaiting_review} icon={Eye} tone={k.tests_awaiting_review > 0 ? "warn" : "default"} testId="kpi-lab-awaiting-review" to="/admin/lab?status=awaiting_review" />
              <DashboardKpi label="Published today" value={k.reports_published_today} icon={FileCheck2} tone="success" testId="kpi-lab-published-today" />
              <DashboardKpi label="Critical detections" value={k.critical_detections_open} icon={Bug} tone={k.critical_detections_open > 0 ? "danger" : "default"} testId="kpi-lab-critical" />
              <DashboardKpi label="Retests required" value={k.retests_required} icon={RotateCcw} tone={k.retests_required > 0 ? "warn" : "default"} testId="kpi-lab-retests" />
              <DashboardKpi label="Overdue > 5 days" value={k.samples_overdue_5d} icon={TimerReset} tone={k.samples_overdue_5d > 0 ? "warn" : "default"} testId="kpi-lab-overdue" />
              <DashboardKpi label="Activity (24h)" value={k.recent_lab_activity_24h} icon={Activity} testId="kpi-lab-activity" />
            </div>

            {/* Workflow funnel */}
            <DashboardSection title="Lab queue by status" subtitle="Current state across all tests" testId="lab-funnel-section">
              <FunnelBar stages={q.lab_funnel} totalLabel="tests in pipeline" />
            </DashboardSection>

            {/* Action queues */}
            <div className="grid lg:grid-cols-2 gap-4 mt-6">
              <QueueTable
                title="Tests awaiting results entry"
                subtitle="Oldest received first"
                rows={q.awaiting_result_entry}
                cols={[
                  { key: "reference", label: "Ref" },
                  { key: "test", label: "Test" },
                  { key: "received_at", label: "Received", format: (v) => formatDateAU(v) },
                ]}
                rowLink={(r) => `/admin/bookings/${r.reference}`}
                empty="Nothing waiting — nice."
                testId="queue-awaiting-entry"
              />
              <QueueTable
                title="Tests missing required fields"
                subtitle="Result rows that look incomplete"
                rows={q.missing_required}
                cols={[
                  { key: "reference", label: "Ref" },
                  { key: "test", label: "Test" },
                  { key: "missing", label: "Missing", format: (v) => (v || []).join(", ") },
                ]}
                rowLink={(r) => `/admin/bookings/${r.reference}`}
                empty="All result rows look complete."
                testId="queue-missing-required"
              />
              <QueueTable
                title="Reports awaiting review"
                subtitle="Sign off and publish"
                rows={q.awaiting_review}
                cols={[
                  { key: "reference", label: "Ref" },
                  { key: "test", label: "Test" },
                  { key: "results_entered_at", label: "Entered", format: (v) => formatDateAU(v) },
                ]}
                rowLink={(r) => `/admin/bookings/${r.reference}`}
                empty="No reports waiting for review."
                testId="queue-awaiting-review"
              />
              <QueueTable
                title="Critical microbiological detections"
                subtitle="E. coli / Coliforms present"
                rows={q.critical_detections}
                cols={[
                  { key: "reference", label: "Ref" },
                  { key: "kind", label: "Detection" },
                  { key: "value", label: "Value" },
                ]}
                rowLink={(r) => `/admin/bookings/${r.reference}`}
                empty="No open critical detections."
                tone="danger"
                testId="queue-critical"
              />
              <QueueTable
                title="Retest reminders due"
                subtitle="Tests flagged for re-run"
                rows={q.retests_due}
                cols={[
                  { key: "reference", label: "Ref" },
                  { key: "test", label: "Test" },
                  { key: "due", label: "Due", format: (v) => formatDateAU(v) },
                ]}
                rowLink={(r) => `/admin/bookings/${r.reference}`}
                empty="No retests pending."
                testId="queue-retests"
              />
              <QueueTable
                title="Recently published reports"
                subtitle="Last 10"
                rows={q.recently_published}
                cols={[
                  { key: "reference", label: "Ref" },
                  { key: "test", label: "Test" },
                  { key: "published_at", label: "Published", format: (v) => formatDateTimeAU(v) },
                ]}
                rowLink={(r) => `/admin/bookings/${r.reference}`}
                empty="No reports published yet."
                testId="queue-recently-published"
              />
            </div>

            <DashboardSection title="Recent lab activity" subtitle="Latest 10 transitions" testId="lab-recent-activity" className="mt-6">
              <ActivityFeed events={data.recent_activity} />
            </DashboardSection>

            <div className="text-[11px] text-slate-400 mt-6 text-right">Generated: {formatDateTimeAU(data.generated_at)}</div>
          </>
        )}
      </div>
    </PortalShell>
  );
}

function QuickAction({ icon: Icon, label, to, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group rounded-xl bg-white border border-slate-200 hover:border-cyan-500 hover:shadow-md transition-all p-3 flex items-center gap-3">
      <Icon className="w-4 h-4 text-cyan-600" />
      <div className="text-xs font-semibold text-slate-800 group-hover:text-cyan-700">{label}</div>
      <ArrowRight className="w-3.5 h-3.5 text-slate-300 ml-auto group-hover:text-cyan-600 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}

function QueueTable({ title, subtitle, rows, cols, rowLink, empty, tone = "default", testId }) {
  const toneCls = tone === "danger" ? "border-rose-200" : "border-slate-200";
  return (
    <section data-testid={testId} className={`bg-white rounded-2xl border ${toneCls} p-5`}>
      <div className="mb-3">
        <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
      </div>
      {!rows?.length ? <EmptyState label={empty || "No data"} /> : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-100">
              {cols.map((c) => <th key={c.key} className="font-medium py-1.5 pr-3">{c.label}</th>)}
              <th className="w-6" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-50 last:border-0">
                {cols.map((c) => (
                  <td key={c.key} className="py-1.5 pr-3 text-slate-800">{c.format ? c.format(r[c.key]) : (r[c.key] ?? "—")}</td>
                ))}
                <td><Link to={rowLink(r)} className="text-cyan-700 inline-flex"><ArrowRight className="w-3.5 h-3.5" /></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
